// Device-level appearance preferences: theme (auto / light / dark), text size
// (auto / fixed steps) and "review invoices before print/email".
// Stored in localStorage on this device only — they are not part of your
// profiles or backups, so a phone and a laptop can differ.
import { useState, useEffect, useCallback } from "react";

const KEY = "bb_prefs_v1";
export const DEFAULT_PREFS = { theme: "auto", textSize: "auto", reviewInvoices: true };

// Multipliers on top of the browser's own default text size.
export const TEXT_STEPS = { normal: 1, large: 1.15, larger: 1.3, largest: 1.5 };
export const PAGE_BG = { dark: "#0B120F", light: "#E9E0CE" };
export const THEME_COLOR = { dark: "#0F0B14", light: "#F7F1E6" };

export function loadPrefs() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const p = JSON.parse(raw);
    return {
      theme: ["auto", "light", "dark"].includes(p.theme) ? p.theme : "auto",
      textSize: p.textSize === "auto" || p.textSize in TEXT_STEPS ? p.textSize : "auto",
      reviewInvoices: p.reviewInvoices !== false,
    };
  } catch (e) {
    return { ...DEFAULT_PREFS };
  }
}

function savePrefs(p) {
  try { localStorage.setItem(KEY, JSON.stringify(p)); } catch (e) { /* private mode: still works for this session */ }
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

// "Auto" text size.
//  • Android / Windows / Mac / Linux browsers already scale rem-based text when
//    the system or browser font size is changed, so we leave the root alone (1).
//  • iPhone / iPad Safari ignores that setting for web pages, but exposes
//    Dynamic Type through the -apple-system-body font keyword, so we read it.
export function measureDeviceScale() {
  try {
    if (typeof document === "undefined" || !isIOS()) return 1;
    if (!(window.CSS && CSS.supports && CSS.supports("font", "-apple-system-body"))) return 1;
    const el = document.createElement("span");
    el.textContent = "A";
    el.style.cssText = "position:absolute;visibility:hidden;font:-apple-system-body;";
    document.body.appendChild(el);
    const px = parseFloat(getComputedStyle(el).fontSize);
    el.remove();
    if (!px || !isFinite(px)) return 1;
    return Math.min(2, Math.max(0.85, px / 17)); // 17px = iOS default body size
  } catch (e) {
    return 1;
  }
}

export function applyToDocument(theme, scale) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
  root.style.fontSize = `${Math.round(scale * 10000) / 100}%`;
  root.style.background = PAGE_BG[theme];
  document.body.style.background = PAGE_BG[theme];
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) { meta = document.createElement("meta"); meta.name = "theme-color"; document.head.appendChild(meta); }
  meta.setAttribute("content", THEME_COLOR[theme]);
}

export function useAppearance() {
  const [prefs, setPrefsState] = useState(loadPrefs);
  const [systemDark, setSystemDark] = useState(() => {
    try { return window.matchMedia("(prefers-color-scheme: dark)").matches; } catch (e) { return true; }
  });
  const [deviceScale, setDeviceScale] = useState(() => measureDeviceScale());
  const [effective, setEffective] = useState(1);

  // follow the operating system live (e.g. sunset auto-switch)
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia("(prefers-color-scheme: dark)"); } catch (e) { return undefined; }
    const on = (e) => setSystemDark(e.matches);
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on); };
  }, []);

  // pick up a Dynamic Type change made while the app was in the background
  useEffect(() => {
    const re = () => setDeviceScale(measureDeviceScale());
    window.addEventListener("focus", re);
    document.addEventListener("visibilitychange", re);
    return () => { window.removeEventListener("focus", re); document.removeEventListener("visibilitychange", re); };
  }, []);

  const theme = prefs.theme === "auto" ? (systemDark ? "dark" : "light") : prefs.theme;
  const scale = prefs.textSize === "auto" ? deviceScale : TEXT_STEPS[prefs.textSize];

  useEffect(() => {
    applyToDocument(theme, scale);
    const px = parseFloat(getComputedStyle(document.documentElement).fontSize);
    if (px) setEffective(px / 16);
  }, [theme, scale]);

  const setPrefs = useCallback((patch) => {
    setPrefsState((p) => { const n = { ...p, ...patch }; savePrefs(n); return n; });
  }, []);

  return { prefs, setPrefs, theme, effectiveScale: effective };
}
